import { deleteEntity, updateEntities } from '@redux/entities'
import { Schemas } from '@redux/schema'
import { normalize } from 'normalizr'

import api from '@functions/api'

import type { SubscriberModel } from '@models/types'
import type {
  Action, DeleteParams, EntityOptions, Errors,
} from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

import { handleError } from '../utils'

const CREATE_REQUEST = 'realbase/subscriber/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/subscriber/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/subscriber/CREATE_FAILURE'

const DELETE_REQUEST = 'realbase/subscriber/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/subscriber/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/subscriber/DELETE_FAILURE'

export type SubscriberModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
}

// Initial State
const initialState: SubscriberModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
}

// Actions

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function createSubscriber(subscriber: Partial<SubscriberModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      subscriber,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/subscribers.json', options, config)
      .then(({ data }: { data: SubscriberModel }) => {
        const normalizedJson = normalize(data, Schemas.SUBSCRIBER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteSubscriber(subscriber: DeleteParams<SubscriberModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/subscribers/${subscriber.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.SUBSCRIBER)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: SubscriberModuleState = initialState,
  action: Action = { type: '' },
): SubscriberModuleState {
  switch (action.type){
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
